<template>
  <div>
    <PageHeader :title="t('title')" />
    <SearchToolbar
      class="mb-4"
      :placeholder="t('search_placeholder')"
      :available-filters="availableFilters"
      :selected-filters="selectedFilters"
      :initial-keywords="keywords"
      @update-filter-search="updateFilterSearch"
      @update-keyword-search="updateKeywordSearch"
    />
    <Loading v-if="loading" />
    <div v-else-if="emailLogs.length === 0" class="text-center mt-4 py-2">
      <p>{{ t("no_results") }}</p>
    </div>
    <div v-else>
      <Table :columns="columns" :data="emailLogs">
        <template #status="{ row }">
          <span
            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            :class="statusBadgeClass(row.status)"
          >
            {{ t(`status_${row.status}`) }}
          </span>
        </template>
        <template #openedAt="{ row }">
          {{ row.openedAt || t("not_opened") }}
        </template>
      </Table>
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        :totalCount="totalCount"
        @changePage="updatePage"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { PageHeader, Loading, SearchToolbar, Table, Pagination } from "@estaly/ui";
import { useI18n } from "vue-i18n";

const EMAIL_LOG_STATUSES = ["pending", "delivered", "bounce", "processed", "dropped", "deferred"];

export default {
  setup() {
    const { t } = useI18n();
    return { t };
  },

  components: {
    PageHeader,
    Loading,
    SearchToolbar,
    Table,
    Pagination,
  },

  data() {
    return {
      currentPage: 1,
      loading: true,
    };
  },

  computed: {
    ...mapGetters("emailLogs", ["emailLogs", "totalPages", "totalCount"]),
    ...mapState("emailLogs", ["selectedFilters", "keywords"]),

    availableFilters() {
      return {
        status: EMAIL_LOG_STATUSES.map((status) => ({
          label: this.t(`status_${status}`),
          value: status,
        })),
      };
    },

    columns() {
      return [
        { label: this.t("column_to"), field: "to" },
        { label: this.t("column_subject"), field: "subject" },
        { label: this.t("column_status"), field: "status", slot: "status" },
        { label: this.t("column_sent_at"), field: "createdAt" },
        { label: this.t("column_opened_at"), field: "openedAt", slot: "openedAt" },
      ];
    },
  },

  watch: {
    "$route.query.page"(newPage) {
      if (newPage === undefined) {
        this.currentPage = 1;
        return;
      }
      this.currentPage = parseInt(newPage) || 1;
    },
    currentPage() {
      this.retrieveEmailLogs();
    },
  },

  async created() {
    const page = this.$route.query.page;
    this.currentPage = page ? parseInt(page) || 1 : 1;
    await this.retrieveEmailLogs();
  },

  methods: {
    ...mapActions("emailLogs", ["fetchIndex", "updateFilter", "updateSearch"]),

    async retrieveEmailLogs() {
      this.loading = true;
      try {
        const params = { page: this.currentPage, keywords: this.keywords, ...this.selectedFilters };
        await this.fetchIndex(params);
      } finally {
        this.loading = false;
      }
    },

    updateFilterSearch(filterName, filterOptions) {
      this.updateFilter({ filterName, filterOptions });
      if (this.currentPage !== 1) {
        this.updatePage(1);
      } else {
        this.retrieveEmailLogs();
      }
    },

    updateKeywordSearch(keyword) {
      this.updateSearch({ searchString: keyword });
      if (this.currentPage !== 1) {
        this.updatePage(1);
      } else {
        this.retrieveEmailLogs();
      }
    },

    updatePage(page) {
      const query = { ...this.$route.query, page };
      this.$router.replace({ query });
    },

    statusBadgeClass(status) {
      switch (status) {
        case "delivered":
          return "bg-green-100 text-green-800";
        case "bounce":
        case "dropped":
          return "bg-red-100 text-red-800";
        case "pending":
        case "processed":
        case "deferred":
          return "bg-yellow-100 text-yellow-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    },
  },
};
</script>

<i18n src="./EmailLogsList.i18n.json"></i18n>
